import { Button, Form, Input, Layout, Select, Statistic, Typography } from 'antd';
import auth from 'api/auth';
import axios, { CancelTokenSource } from 'axios';
import { languagesValues } from 'routes/Main/Main';
import { saveTokensAfterAuthentication } from 'helpers/cookies';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import classes from './TFAuth.module.scss';
import LogoType from 'assets/img';
import { useAppSelector } from 'store/hook';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';


const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
const loginAdress = authType === 'blitz' ? 'blitz-auth' : 'login';
const expiredMinutes = 10;

export default function OTPTFAuth() {
  const [otp, setOtp] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRequestingCode, setIsRequestingCode] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);
  const [deadLine, setDeadLine] = React.useState<number>(0);

  const { messageApi } = useAppSelector((store) => store.windowState);  
  
  const cancelTokenRef = React.useRef<CancelTokenSource | null>(null);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [form] = Form.useForm();
  const { email = '' } = useParams();

  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  React.useEffect(() => {
    setDeadLine(Date.now() + expiredMinutes * 60 * 1000);
  }, []);

  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
    //to translate touched fields when switching
    const allFormFields = form.getFieldsValue(true);
    const namesOfFilledFields = Object.keys(allFormFields);
    form.validateFields(namesOfFilledFields);
  };

  const onSubmit = async (values: { otp: string }) => {
    try {
      setIsLoading(true);
      const browserData = await functionCollectBrowserData();
      cancelTokenRef.current = axios.CancelToken.source();
      const formData = new FormData();
      formData.append('auth_password', values.otp);
      formData.append('email', email);
      formData.append('browser_data', JSON.stringify(browserData) || '');
      const response = await auth.verifyAuth(
        { auth_password: values.otp, email, browser_data: JSON.stringify(browserData) || '' },
        formData,
        cancelTokenRef.current.token
      );

      saveTokensAfterAuthentication(response.data); 

      localStorage.removeItem('tfaArgument');
      messageApi?.destroy();
      navigate('/');
    } catch (err: any) {
      if (err?.message === 'Aborted' || err?.name === 'CanceledError') return;
      messageApi?.error(t('login.modals.verify.wrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    messageApi?.warning(t('login.modals.verify.wrong'));
    navigate(`/${loginAdress}`);
  };

  const defaultLanguage = localStorage.getItem('language') || 'en';

  const loginRequest = async () => {
    setIsRequestingCode(true);
    try {
      const formData = new FormData();
      formData.append('email', email.toLocaleLowerCase());
      formData.append(
        'details',
        JSON.stringify({
          email: email.toLocaleLowerCase(),
        })
      );
      formData.append('password', window.atob(String(localStorage.getItem('tfaArgument'))));

      await auth.login(formData);
      setCountdown(60);
      messageApi?.success(t('login.modals.verify.resend'))
    } catch (e) {
      console.log(e,'TFA loginRequest error');
    } finally {
      setIsRequestingCode(false);
    }
  };

  //   const onChange = (value: string) => {
  //     cancelTokenRef.current?.cancel();
  //     if (value.length === 6) {
  //       onSubmit(value);
  //     }
  //     setOtp(value);
  //   };

  return (
    <div className={classes.mainWrap}>
      <Layout className={classes.tfaElem}>
        <Select
          className={classes.localizationSelect}
          defaultValue={defaultLanguage}
          onChange={onLanguageChange}
          style={{ minWidth: 130 }}
          options={languagesValues}
          optionLabelProp={'label'}
        />
        <div className={classes.tfaCard}>
          <div className={classes.typography}>
            <img src={LogoType} alt='Logotype' className={classes.logo} />
            <Typography>
              <Typography.Title level={3}>{t('login.modals.verify.header')}</Typography.Title>
              <Typography.Text type='secondary'>{t('login.modals.verify.title')}</Typography.Text>
            </Typography>
          </div>
          <Form onFinish={onSubmit} form={form} className={classes.tfaForm}>
            <Statistic.Countdown
              className={classes.countDown}
              onFinish={onCancel}
              title={t('login.modals.verify.expired')}
              value={deadLine}
              style={{ display: 'none' }}
              format='mm:ss'
            />
            <Form.Item name={'otp'}>
              <Input.OTP
                style={{
                  justifyContent: 'space-between',
                  width: '70%',
                  height: '40px',
                  margin: '20px auto 0',
                  display: 'flex',
                }}
                value={otp}
                // onChange={onChange}
                autoFocus
              />
            </Form.Item>

            <Form.Item shouldUpdate>
              {() => (
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={isLoading}
                  disabled={
                    !form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length
                  }
                >
                  {t('main.confirmButton')}
                </Button>
              )}
            </Form.Item>

            <div className={classes.rerequestWrap}>
              <Button
                type='link'
                onClick={loginRequest}
                disabled={isLoading || countdown > 0}
                className={classes.primary}
                loading={isRequestingCode}
              >
                {t('login.modals.verify.again')}
              </Button>
            </div>

            {countdown > 0 && (
              <div className={classes.timerMessage}>
                {t('login.modals.timer.message', {
                  seconds: countdown,
                })
                  .split('\n')
                  .map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
              </div>
            )}
          </Form>
        </div>
      </Layout>
    </div>
  );
}
